// ============================================================
// DebugPlugin — in-player debug console (plugin entry point)
//
// Combines event logging with the interactive debug overlay UI.
// Enabled by default via `showDebug: true` in PlayerConfig.
//
// What this plugin provides:
//   • Registers the DebugConsole React component into the
//     'debug-console' UIRegistry slot so ReactPlayerShell renders it
//   • Subscribes to ~15 player lifecycle events and forwards them as
//     timestamped 'debug:log' lines consumed by the DebugConsole overlay
//   • Toggle visibility: hotkey ` (Backquote, emits 'debug:toggle')
//
// Opt-out: pass `showDebug: false` in PlayerConfig to disable entirely.
// Manual use: pass `new DebugPlugin()` in `PlayerConfig.plugins` when
//   `showDebug` is false but you still want the console for a specific
//   player instance.
// ============================================================
import type { IPlugin, PluginContext, PlayerError } from '../../core/types';
import { DebugConsole } from './DebugConsole';

export class DebugPlugin implements IPlugin {
  readonly name = 'debug';

  private ctx: PluginContext | null = null;
  private unsubs: Array<() => void> = [];

  init(ctx: PluginContext): void {
    this.ctx = ctx;
    ctx.uiRegistry.register('debug-console', DebugConsole);
    this.bindEvents();
    ctx.logger.debug('DebugPlugin initialized');
  }

  destroy(): void {
    this.ctx?.uiRegistry.unregister('debug-console', DebugConsole);
    this.unsubs.forEach(fn => fn());
    this.unsubs = [];
    this.ctx = null;
  }

  private log(msg: string): void {
    if (!this.ctx) return;
    const now = new Date();
    const ts = now.toISOString().substring(11, 23); // HH:MM:SS.mmm
    this.ctx.eventBus.emit('debug:log', `[${ts}] ${msg}`);
  }

  private bindEvents(): void {
    if (!this.ctx) return;
    const { eventBus, store } = this.ctx;

    this.unsubs = [
      eventBus.on('play', () => this.log('▶ play')),
      eventBus.on('pause', () => this.log('⏸ pause')),
      eventBus.on('error', (e: PlayerError) =>
        this.log(`✖ error [${e.code}] ${e.message}${e.fatal ? ' (fatal)' : ''}`),
      ),
      eventBus.on('stream:online', () => this.log('📡 stream:online')),
      eventBus.on('stream:offline', () => this.log('📡 stream:offline')),
      eventBus.on('engine:ready', () =>
        this.log(`⚙ engine:ready (${store.getState().engineName || '?'})`),
      ),
      eventBus.on('engine:destroyed', () => this.log('⚙ engine:destroyed')),
      eventBus.on('quality:change', ({ level }: { level: string }) =>
        this.log(`🎚 quality → ${level}`),
      ),
      eventBus.on('latency:mode', (mode: 'low' | 'normal') =>
        this.log(`⏱ latency:mode → ${mode}`),
      ),
      eventBus.on('latency:fallback', () => this.log('⏱ latency:fallback')),
      eventBus.on('api:error', (err: { message: string }) =>
        this.log(`✖ api:error: ${err.message}`),
      ),
    ];
  }
}
